import { useEffect, useState } from "react";
import Lucide from "src/base-components/Lucide";
import logo from "src/assets/images/brand/logo.webp";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const INSTALL_DISMISSED_KEY = "pwa-install-dismissed";

type InstallPlatform = "ios" | "android" | "desktop";

const isStandaloneApp = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;

const getInstallPlatform = (): InstallPlatform => {
  const ua = window.navigator.userAgent.toLowerCase();
  const isIos =
    /iphone|ipad|ipod/.test(ua) ||
    (window.navigator.platform === "MacIntel" &&
      window.navigator.maxTouchPoints > 1);

  if (isIos) {
    return "ios";
  }

  if (/android/.test(ua)) {
    return "android";
  }

  return "desktop";
};

const getInstallCopy = (
  platform: InstallPlatform,
  canPrompt: boolean,
): { title: string; body: string } => {
  if (canPrompt) {
    return {
      title: "Install TravoTera CRM",
      body: "Add TravoTera to your home screen for faster access.",
    };
  }

  if (platform === "ios") {
    return {
      title: "Add TravoTera to Home Screen",
      body: 'Tap Share, then choose "Add to Home Screen".',
    };
  }

  if (platform === "android") {
    return {
      title: "Install TravoTera CRM",
      body: 'Open the Chrome menu (⋮), then tap "Install app".',
    };
  }

  return {
    title: "Install TravoTera CRM",
    body: "Use the install icon in the address bar, or choose Install from the browser menu.",
  };
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>("desktop");

  useEffect(() => {
    if (isStandaloneApp()) {
      return;
    }

    if (localStorage.getItem(INSTALL_DISMISSED_KEY) === "true") {
      return;
    }

    const detectedPlatform = getInstallPlatform();
    setPlatform(detectedPlatform);

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const handleAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.setItem(INSTALL_DISMISSED_KEY, "true");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    const timer = window.setTimeout(
      () => {
        if (!isStandaloneApp()) {
          setVisible(true);
        }
      },
      detectedPlatform === "ios" ? 1200 : 3500,
    );

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setVisible(false);
      localStorage.setItem(INSTALL_DISMISSED_KEY, "true");
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, "true");
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  const copy = getInstallCopy(platform, Boolean(deferredPrompt));

  return (
    <div className="fixed bottom-4 left-4 z-[9980] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_18px_40px_-20px_rgba(15,23,42,0.45)] sm:bottom-6 sm:left-6">
      <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
          <img
            src={logo}
            alt="TravoTera"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {copy.title}
          </p>
          <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Progressive Web App
          </p>
        </div>
        <button
          type="button"
          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-600"
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
        >
          <Lucide icon="X" className="h-4 w-4" />
        </button>
      </div>

      <div className="px-4 py-3.5">
        <p className="text-sm leading-relaxed text-slate-600">{copy.body}</p>

        {platform === "ios" && !deferredPrompt ? (
          <ol className="mt-3 space-y-1.5 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-3 text-xs text-slate-600">
            <li className="flex gap-2">
              <span className="font-semibold text-primary">1.</span>
              <span>
                Tap <span className="font-semibold">Share</span> in Safari
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary">2.</span>
              <span>
                Tap{" "}
                <span className="font-semibold">Add to Home Screen</span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary">3.</span>
              <span>
                Tap <span className="font-semibold">Add</span>
              </span>
            </li>
          </ol>
        ) : null}

        <div className="mt-4 flex gap-2">
          {deferredPrompt ? (
            <button
              type="button"
              onClick={handleInstall}
              className="flex-1 rounded-lg bg-primary px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-primary/90"
            >
              Install app
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDismiss}
              className="flex-1 rounded-lg bg-primary px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-primary/90"
            >
              Got it
            </button>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
